import React from 'react';
import { ChevronRight, ShoppingCart, DollarSign, FileText, Clock, Truck, Shield, BarChart } from 'lucide-react';

interface BuyersPageProps {
  setCurrentPage: (page: string) => void;
}

const BuyersPage = ({ setCurrentPage }: BuyersPageProps) => (
  <div>
    <div className="relative bg-gradient-to-r from-blue-600 to-blue-800">
      <div className="max-w-7xl mx-auto py-24 px-4">
        <div className="text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
            For Buyers
            <span className="block text-blue-200">Streamline Your Beverage Procurement</span>
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-xl text-blue-100">
            Access a comprehensive marketplace of beverage products with exclusive discounts and streamlined ordering.
          </p>
        </div>
      </div>
    </div>

    <div className="max-w-7xl mx-auto py-16 px-4">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">One-Stop Shop for All Beverage Needs</h2>
          <p className="mt-4 text-lg text-gray-600">
            Access thousands of products from verified suppliers all in one place. From craft beer to premium spirits,
            find everything you need for your business.
          </p>
          <ul className="mt-8 space-y-4">
            <li className="flex items-start">
              <div className="flex-shrink-0">
                <ShoppingCart className="h-6 w-6 text-blue-600" />
              </div>
              <p className="ml-3 text-base text-gray-700">
                Browse and order from multiple suppliers in a single transaction
              </p>
            </li>
            <li className="flex items-start">
              <div className="flex-shrink-0">
                <Shield className="h-6 w-6 text-blue-600" />
              </div>
              <p className="ml-3 text-base text-gray-700">
                All suppliers are pre-vetted and verified for quality and reliability
              </p>
            </li>
            <li className="flex items-start">
              <div className="flex-shrink-0">
                <BarChart className="h-6 w-6 text-blue-600" />
              </div>
              <p className="ml-3 text-base text-gray-700">
                Real-time inventory tracking and product availability
              </p>
            </li>
          </ul>
        </div>
        <div className="bg-gray-50 p-8 rounded-lg">
          <h3 className="text-xl font-semibold text-gray-900">Featured Products</h3>
          <div className="mt-6 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Craft Beer Selection</span>
              <span className="text-blue-600 font-medium">20% Off</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Premium Spirits</span>
              <span className="text-blue-600 font-medium">15% Off</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Wine Collection</span>
              <span className="text-blue-600 font-medium">10% Off</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900">Exclusive Benefits for Buyers</h2>
          <p className="mt-4 text-xl text-gray-600">Save time and money with our comprehensive buyer solutions</p>
        </div>
        
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex justify-center">
              <DollarSign className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900">Competitive Pricing</h3>
            <p className="mt-2 text-sm text-gray-500">Access exclusive discounts and bulk pricing options</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex justify-center">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900">Electronic Payments</h3>
            <p className="mt-2 text-sm text-gray-500">Secure and efficient payment processing</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex justify-center">
              <Clock className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900">Flexible Scheduling</h3>
            <p className="mt-2 text-sm text-gray-500">Choose delivery times that work for your business</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex justify-center">
              <Truck className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900">Reliable Delivery</h3>
            <p className="mt-2 text-sm text-gray-500">GPS-tracked deliveries with real-time updates</p>
          </div>
        </div>
      </div>
    </div>

    <div className="max-w-7xl mx-auto py-16 px-4">
      <div className="bg-blue-600 rounded-lg shadow-xl overflow-hidden">
        <div className="px-6 py-8 sm:p-10">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white">Ready to Start Saving?</h2>
            <p className="mt-4 text-xl text-blue-100">
              Join hundreds of businesses already reducing their beverage costs with BottleCosts
            </p>
            <div className="mt-8">
              <button onClick={() => setCurrentPage('contact')}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50">
                Get Started Today
                <ChevronRight className="ml-2 h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default BuyersPage; 